import { CartesianGrid, Legend, Line, LineChart, ResponsiveContainer, XAxis, YAxis, Tooltip } from "recharts";
import { formatter } from "../../utils/formatter";
import { ILegend, ITooltip } from "../../services/interface/IForecast";
import FeatureImportanceTable from "../FeatureImportanceTable/featureImportanceTable";
import { useState } from "react";

const CustomLegend = (props: any) => {
    const { payload } = props;
    return (
        <ul className="flex items-center gap-2">
            {payload.map((entry: ILegend, index: number) => (
                <li key={`item-${index}`} className="flex items-center mb-1">
                    <svg width="40" height="10">
                        <line x1="0" y1="5" x2="15" y2="5" stroke={entry.color} strokeWidth="2" />
                        <polygon points="20,1 24,5 20,9 16,5" fill={entry.color} />
                        <line x1="25" y1="5" x2="40" y2="5" stroke={entry.color} strokeWidth="2" />
                    </svg>
                    <span style={{ color: entry.color }} className="ml-2 font-semibold chart-legend">{entry.value}</span>
                </li>
            ))}
        </ul>
    );
};

const CustomTooltip = (props: any) => {
    const { active, payload } = props;
    if (active && payload && payload.length) {
        return (
            <div className="bg-[#fff] border-[1px] border-[#BEBEBE] p-2 rounded rs-bg">
                <div className="flex flex-row gap-2">
                    {payload?.map(($: ITooltip) => (
                        <div className="flex flex-col" key={$?.name}>
                            <p className="text-black chart-subtitle">
                                {String($?.name).split("(")[0]}
                            </p>
                            <p style={{color:$.color}} className="text-black text-fs20 font-bold">
                                {formatter($?.value)}
                            </p>
                        </div>
                    ))}
                </div>
            </div>
        );
    }
    return null;
};

const FeatureCard = (props: any) => {
    const { title, accuracyData, tableData, dataName, dataKey, data } = props;

    const [selectedData, setSelectedData] = useState(null);
    const [selectedTable, setSelectedTable] = useState<number|null>(null);

    const handleCellClick = (data: any, tableIndex: number) => {
        if (selectedData === data && selectedTable === tableIndex) {
          // If the clicked cell is the same as the currently selected cell, reset the state
          setSelectedData(null);
          setSelectedTable(null);
        } else {
          // Otherwise, update the state with the clicked cell's data and table index
          setSelectedData(data);
          setSelectedTable(tableIndex);
        }
    };

    const extractAccuracyValues = (data: any) => {
        let values:string[] = ["-"];
        data?.forEach((monthData: { [x: string]: any; hasOwnProperty: (arg0: string) => any; }) => {
          for (const month in monthData) {
            if (monthData.hasOwnProperty(month)) {
              const accuracyObj = monthData[month];
              for (const key in accuracyObj) {
                if (accuracyObj.hasOwnProperty(key)) {
                  const value = accuracyObj[key];
                  values.push((value * 100).toFixed(2)); // Convert to percentage and format to 2 decimal places
                }
              }
            }
          }
        });
        return values;
    };

    const periodData = [
        "November 2023",
        "December 2023",
        "January 2024",
        "February 2024"
    ]

    const accuracyValues = extractAccuracyValues(accuracyData);
    const targetDates = data.map((entry: any, index: number)=> (index % 4 === 0 ? entry?.forecastDate : null))
    .filter(Boolean);

    return (
        <>
            <div className="w-[100%] h-[100%] bg-[#FFF] 
            shadow rounded px-[1%] py-[10px] border-[1px] border-[#BEBEBE] flex flex-col">
                <div className="flex justify-between items-center mb-[0.5vw]">
                    <span className="text-mainGreen font-semibold chart-title self-start">
                        {title}
                    </span>
                </div>
                <div className="flex h-[42%]">
                    <div className="grow h-full">
                        <ResponsiveContainer width={"100%"} height={"100%"}>
                            <LineChart data={data} margin={{ top: 20, right: 15, bottom: 10, left: 0 }}>
                                {["/FeatureImportance"].includes(location.pathname) && (
                                    <CartesianGrid
                                        vertical={true}
                                        horizontal={false}
                                        syncWithTicks={true}
                                    />
                                )}
                                <XAxis
                                    dataKey="forecastDate"
                                    type="category"
                                    stroke="#949494"
                                    ticks={targetDates}
                                    tickFormatter={(value:any,index:number)=>
                                        targetDates.includes(value) ? value : ""
                                    }
                                    hide
                                    interval={0}
                                />                                
                                <YAxis interval="equidistantPreserveStart" fontSize={"0.80vw"} tickCount={10} tickFormatter={formatter} stroke="#949494" />
                                <Tooltip content={<CustomTooltip/>} />
                                <Legend
                                    content={<CustomLegend />}
                                    align="left"
                                    verticalAlign="top"
                                    iconSize={8}
                                    wrapperStyle={{ left: 0, top: 0, marginBottom: "10px" }}
                                />
                                <Line
                                    name={dataName[0]}
                                    dataKey={dataKey[0]}
                                    stroke="#012169"
                                    fill="#012169"
                                    strokeWidth={2}
                                    dot={{ strokeWidth: 1, r: 2 }}
                                    activeDot={{ stroke: '#012169', fill: 'white', strokeWidth: 2, r: 4 }}
                                />
                                <Line
                                    name={dataName[1]}
                                    dataKey={dataKey[1]}
                                    stroke="#86BC25"
                                    fill="#86BC25"
                                    strokeWidth={2}
                                    dot={{ strokeWidth: 1, r: 2 }}
                                    activeDot={{ stroke: '#86BC25', fill: 'white', strokeWidth: 2, r: 4 }}
                                />
                            </LineChart>
                        </ResponsiveContainer>
                    </div>
                </div>
                <div className="flex flex-col h-[50%] px-[1%] 
                    md:pl-[2.8%] md:pr-[1.8%] 
                    lg:pl-[1.5%] lg:pr-[1.3%] 
                    xl:pl-[1%] xl:pr-[1%] 
                    2xl:px-[0px] 2xl:pr-[0.8%]">
                    <div className="grid grid-cols border border-[1px]">
                        <div className="flex flex-col justify-center text-left px-1 bg-white font-semibold border border-[1px]">
                            <span className="chart-subtitle font-semibold">Period</span>
                        </div>
                        {periodData.map((col, index) => (
                            <div key={index} className="grow text-center chart-subtitle font-semibold border border-[1px]">
                                {col}
                            </div>
                        ))}
                        <div className="flex flex-col justify-center text-left px-1 bg-white font-semibold border border-[1px]">
                            <span className="chart-subtitle font-semibold">Accuracy</span>
                        </div>
                        {accuracyValues.map((col, index) => (
                            <div key={index} className="grow text-center chart-subtitle font-semibold border border-[1px]">
                                {col}%
                            </div>
                        ))}
                    </div>
                    <div className="flex grow h-[50%]">
                        <div className="flex flex-col justify-center text-left px-1 bg-deloitteGreen font-semibold border-r border-[#BEBEBE]">
                            <span className="chart-subtitle">Key</span>
                            <span className="chart-subtitle">Demand&nbsp;</span>
                            <span className="chart-subtitle">Drivers</span>
                        </div>
                        {/* Feature Importance Details Component */}
                        <div className="w-[25%] border border-[1px] overflow-y-scroll">
                            {(selectedTable === null || selectedTable == 1) && tableData?.qrt1Data?.map((col: any, index: number) => (
                                <FeatureImportanceTable 
                                    // onClick={() => handleCellClick(col, 1)}
                                    label={col["Qtr1_Title"]} 
                                    value={col["Qtr1_Shap"]} 
                                    index={index} 
                                />
                            ))}
                        </div>
                        <div className="w-[25%] border border-[1px] overflow-y-scroll">
                            {(selectedTable === null || selectedTable == 2) && tableData?.qrt2Data?.map((col: any, index: number) => (
                                <FeatureImportanceTable
                                    // onClick={() => handleCellClick(col, 2)}
                                    label={col["Qtr2_Title"]}
                                    value={col["Qtr2_Shap"]}
                                    index={index}
                                />
                            ))}
                        </div>
                        <div className="w-[25%] border border-[1px] overflow-y-scroll">
                            {(selectedTable === null || selectedTable == 3) && tableData?.qrt3Data?.map((col: any, index: number) => (
                                <FeatureImportanceTable
                                    // onClick={() => handleCellClick(col, 3)}
                                    label={col["Qtr3_Title"]}
                                    value={col["Qtr3_Shap"]}
                                    index={index}
                                />
                            ))}
                        </div>
                        <div className="w-[25%] border border-[1px] overflow-y-scroll">
                            {(selectedTable === null || selectedTable == 4) && tableData?.qrt4Data?.map((col: any, index: number) => (
                                <FeatureImportanceTable
                                    // onClick={() => handleCellClick(col, 4)}
                                    label={col["Qtr4_Title"]}
                                    value={col["Qtr4_Shap"]}
                                    index={index}
                                />
                            ))}
                        </div>
                    </div>
                </div>
            </div>
        </>
    );
};

export default FeatureCard;
